import React, { useState, useRef, useEffect } from 'react';
import { usePlaytesterStore } from '../../stores/playtester-store.js';

// ── LifeControl ───────────────────────────────────────────────────────────────

function LifeControl() {
  const lifeTotal = usePlaytesterStore((s) => s.lifeTotal);
  const adjustLife = usePlaytesterStore((s) => s.adjustLife);
  const setLife = usePlaytesterStore((s) => s.setLife);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  function startEdit() {
    setDraft(String(lifeTotal));
    setEditing(true);
  }

  function commitEdit() {
    const v = parseInt(draft, 10);
    if (!isNaN(v)) setLife(v);
    setEditing(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') setEditing(false);
  }

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  return (
    <div className="flex items-center gap-1">
      <span className="text-xs text-gray-500 mr-1">Life</span>
      <button
        onClick={() => adjustLife(-1)}
        className="w-6 h-6 flex items-center justify-center rounded bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm font-bold transition"
        aria-label="Decrease life"
      >
        −
      </button>
      {editing ? (
        <input
          ref={inputRef}
          type="number"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={handleKeyDown}
          className="w-14 text-center text-white font-bold text-lg bg-gray-800 border border-orange-500 rounded outline-none px-1"
        />
      ) : (
        <button
          onClick={startEdit}
          className={`w-14 text-center font-bold text-lg rounded px-1 transition ${
            lifeTotal <= 5 ? 'text-red-400' : lifeTotal <= 10 ? 'text-yellow-400' : 'text-white'
          }`}
          aria-label={`Life total: ${lifeTotal}. Click to edit.`}
        >
          {lifeTotal}
        </button>
      )}
      <button
        onClick={() => adjustLife(1)}
        className="w-6 h-6 flex items-center justify-center rounded bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm font-bold transition"
        aria-label="Increase life"
      >
        +
      </button>
    </div>
  );
}

// ── PlayerCountersDropdown ────────────────────────────────────────────────────

type CounterKey = 'poison' | 'energy' | 'experience';

const COUNTER_LABELS: Record<CounterKey, string> = {
  poison: 'Poison',
  energy: 'Energy',
  experience: 'Experience',
};

function PlayerCountersDropdown() {
  const playerCounters = usePlaytesterStore((s) => s.playerCounters);
  const adjustPlayerCounter = usePlaytesterStore((s) => s.adjustPlayerCounter);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const hasAny = Object.values(playerCounters).some((v) => v > 0);

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`px-2 py-1 rounded text-xs font-medium transition border ${
          hasAny
            ? 'border-orange-500/60 text-orange-400 bg-orange-950/30'
            : 'border-gray-700 text-gray-400 bg-gray-800 hover:bg-gray-700'
        }`}
      >
        Counters{hasAny ? ` (${Object.values(playerCounters).reduce((a, b) => a + b, 0)})` : ''}
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-[110] bg-gray-900 border border-gray-700 rounded-lg shadow-xl p-3 flex flex-col gap-2 min-w-[160px]">
          {(Object.keys(COUNTER_LABELS) as CounterKey[]).map((key) => (
            <div key={key} className="flex items-center justify-between gap-2">
              <span className="text-xs text-gray-400">{COUNTER_LABELS[key]}</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => adjustPlayerCounter(key, -1)}
                  className="w-5 h-5 flex items-center justify-center rounded bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-bold transition"
                >
                  −
                </button>
                <span className="w-6 text-center text-white text-sm font-bold">
                  {playerCounters[key]}
                </span>
                <button
                  onClick={() => adjustPlayerCounter(key, 1)}
                  className="w-5 h-5 flex items-center justify-center rounded bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-bold transition"
                >
                  +
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── TurnCounter ───────────────────────────────────────────────────────────────

function TurnCounter() {
  const turnNumber = usePlaytesterStore((s) => s.turnNumber);
  const setTurnNumber = usePlaytesterStore((s) => s.setTurnNumber);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  function startEdit() {
    setDraft(String(turnNumber));
    setEditing(true);
  }

  function commitEdit() {
    const v = parseInt(draft, 10);
    if (!isNaN(v)) setTurnNumber(v);
    setEditing(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') setEditing(false);
  }

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  return (
    <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-800 rounded border border-gray-700">
      <span className="text-xs text-gray-500">Turn</span>
      {editing ? (
        <input
          ref={inputRef}
          type="number"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={handleKeyDown}
          className="w-10 text-center text-white font-bold text-sm bg-gray-800 border border-orange-500 rounded outline-none px-1"
        />
      ) : (
        <button
          onClick={startEdit}
          className="text-sm font-bold text-white hover:text-orange-400 transition"
          title="Click to edit turn number"
          aria-label={`Turn ${turnNumber}, click to edit`}
        >
          {turnNumber}
        </button>
      )}
    </div>
  );
}

// ── ZoomControl ───────────────────────────────────────────────────────────────

function ZoomControl() {
  const zoom = usePlaytesterStore((s) => s.zoom);
  const adjustZoom = usePlaytesterStore((s) => s.adjustZoom);

  return (
    <div className="flex items-center gap-0.5">
      <button
        onClick={() => adjustZoom(-10)}
        className="w-6 h-6 flex items-center justify-center rounded bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white text-sm font-bold transition border border-gray-700 disabled:opacity-40"
        disabled={zoom <= 60}
        title="Zoom out (10%)"
        aria-label="Zoom out"
      >
        −
      </button>
      <span className="text-xs text-gray-400 font-medium w-10 text-center tabular-nums">
        {zoom}%
      </span>
      <button
        onClick={() => adjustZoom(10)}
        className="w-6 h-6 flex items-center justify-center rounded bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white text-sm font-bold transition border border-gray-700 disabled:opacity-40"
        disabled={zoom >= 220}
        title="Zoom in (10%)"
        aria-label="Zoom in"
      >
        +
      </button>
    </div>
  );
}

// ── ExitButton ────────────────────────────────────────────────────────────────

function ExitButton() {
  const exitGame = usePlaytesterStore((s) => s.exitGame);
  return (
    <button
      onClick={exitGame}
      className="px-3 py-1 text-sm font-medium text-gray-300 hover:text-white bg-gray-800 hover:bg-red-900/60 border border-gray-700 hover:border-red-700 rounded transition"
    >
      Exit
    </button>
  );
}

// ── PlaytesterTopBar ──────────────────────────────────────────────────────────

interface PlaytesterTopBarProps {
  onShowHordeSetup?: () => void;
}

export default function PlaytesterTopBar({ onShowHordeSetup }: PlaytesterTopBarProps) {
  const deckName = usePlaytesterStore((s) => s.deckName);
  const horde = usePlaytesterStore((s) => s.horde);

  return (
    <div className="shrink-0 h-12 bg-gray-900/80 border-b border-gray-800 flex items-center px-3 gap-3">
      {/* Deck name */}
      <span className="text-sm font-semibold text-gray-200 truncate max-w-[180px]" title={deckName}>
        {deckName}
      </span>

      <div className="w-px h-6 bg-gray-700 shrink-0" />

      {/* Life total */}
      <LifeControl />

      <div className="w-px h-6 bg-gray-700 shrink-0" />

      {/* Player counters */}
      <PlayerCountersDropdown />

      <div className="w-px h-6 bg-gray-700 shrink-0" />

      {/* Turn counter */}
      <TurnCounter />

      {/* Spacer */}
      <div className="flex-1" />

      {/* vs Horde button (only when no horde session active) */}
      {!horde && onShowHordeSetup && (
        <button
          onClick={onShowHordeSetup}
          className="px-3 py-1 rounded bg-orange-800 hover:bg-orange-700 text-orange-100 text-xs font-medium transition-colors border border-orange-700"
        >
          vs Horde
        </button>
      )}

      {/* Zoom control */}
      <ZoomControl />

      <div className="w-px h-6 bg-gray-700 shrink-0" />

      {/* Exit */}
      <ExitButton />
    </div>
  );
}
